import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { NextFormContext } from '../../contexts/NextFormContext'
import { UserWorkspacesContext } from '../../contexts/UserWorkspacesContext'
import { CommonSchema } from '../../schemas'
import { ASSIGN_USER_TO_WORKSPACE, Data, Variables } from './api'
import { userWorkspaceFromDTO } from './mapper'
import { UseInvitePublisherToWorkspace, UseInvitePublisherToWorkspaceParams } from './types'

export const useInvitePublisherToWorkspace = ({
  userId,
}: UseInvitePublisherToWorkspaceParams): UseInvitePublisherToWorkspace => {
  const { t } = useTranslation('features/user')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<CommonSchema>>({})
  const { append } = useContext(UserWorkspacesContext)
  const { setShowNextInputs } = useContext(NextFormContext)

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: ASSIGN_USER_TO_WORKSPACE,
    onCompleted: async (data: Data): Promise<void> => {
      const {
        invitePublisherToWorkspace: { errors, workspace },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.publisher.edit.error'), { variant: 'error' })
      } else if (workspace) {
        setErrors({})
        enqueueSnackbar(t('form.publisher.edit.success'), {
          variant: 'success',
        })

        const userWorkspace = userWorkspaceFromDTO(workspace, userId)

        if (userWorkspace) {
          append(userWorkspace)
          setShowNextInputs(false)
        }
      }
    },
  })

  const invitePublisherToWorkspace = ({ role, userId, workspaceId }: CommonSchema): void => {
    void mutateAsync({
      input: {
        role,
        userId,
        workspaceId: workspaceId.value,
      },
    })
  }

  return {
    errors,
    invitePublisherToWorkspace,
    loading: isPending,
  }
}
